import React, { useState, useRef, useEffect } from "react";
import { Box, Tooltip } from "@mui/material";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { FormattedMessage } from "react-intl";

const DraggableExitFullscreenButton = ({ onExit }) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [isDraggingState, setIsDraggingState] = useState(false);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const initialPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        if (!prev) return null;
        const btnWidth = buttonRef.current?.offsetWidth || 40;
        const btnHeight = buttonRef.current?.offsetHeight || 40;
        const maxX = window.innerWidth - btnWidth;
        const maxY = window.innerHeight - btnHeight;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    const buttonEl = buttonRef.current;
    if (!buttonEl) return;

    const rect = buttonEl.getBoundingClientRect();
    startPosRef.current = { x: e.clientX, y: e.clientY };
    initialPosRef.current = { x: rect.left, y: rect.top };
    isDraggingRef.current = false;

    if (buttonEl.setPointerCapture && e.pointerId !== undefined) {
      try {
        buttonEl.setPointerCapture(e.pointerId);
      } catch (_) {}
    }

    const onPointerMove = (moveEvent) => {
      const dx = moveEvent.clientX - startPosRef.current.x;
      const dy = moveEvent.clientY - startPosRef.current.y;

      if (!isDraggingRef.current && Math.hypot(dx, dy) > 3) {
        isDraggingRef.current = true;
        setIsDraggingState(true);
      }

      if (isDraggingRef.current) {
        const btnWidth = rect.width || 40;
        const btnHeight = rect.height || 40;

        let newX = initialPosRef.current.x + dx;
        let newY = initialPosRef.current.y + dy;

        const maxX = window.innerWidth - btnWidth;
        const maxY = window.innerHeight - btnHeight;

        newX = Math.max(0, Math.min(maxX, newX));
        newY = Math.max(0, Math.min(maxY, newY));

        setPosition({ x: newX, y: newY });
      }
    };

    const onPointerUp = (upEvent) => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);

      if (buttonEl.releasePointerCapture && upEvent.pointerId !== undefined) {
        try {
          buttonEl.releasePointerCapture(upEvent.pointerId);
        } catch (_) {}
      }

      const dragged = isDraggingRef.current;
      isDraggingRef.current = false;
      setIsDraggingState(false);

      if (!dragged) {
        onExit?.();
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  };

  return (
    <Tooltip
      title={
        <FormattedMessage
          id="liveview.videowall.exitFullscreen"
          defaultMessage="Exit Fullscreen"
        />
      }
    >
      <Box
        ref={buttonRef}
        onPointerDown={handlePointerDown}
        sx={{
          position: "fixed",
          zIndex: 99999,
          ...(position
            ? { left: `${position.x}px`, top: `${position.y}px` }
            : { top: "20px", right: "20px" }),
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isDraggingState ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
          transition: isDraggingState
            ? "none"
            : "background-color 0.2s, transform 0.1s",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            transform: isDraggingState ? "none" : "scale(1.05)",
          },
        }}
      >
        <FullscreenExitIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

export default DraggableExitFullscreenButton;
